import React, { useState, useEffect } from 'react';
import { t } from '../tokens';
import { Measure as MeasureType, ActiveChord, TimeSignature, AppMode } from '../types';
import ChordCell from './ChordCell';
import MeasureAnnotations from './MeasureAnnotations';
import StyledChord from './StyledChord';

interface MeasureProps {
  mode: AppMode;
  measure: MeasureType;
  measureIndex: number;
  activeChord: ActiveChord | null;
  isPlaying: boolean;
  playingChordIndex: number | null;
  isStartMeasure: boolean;
  invalidChords: Record<string, string | null>;
  clipboard: (string | null)[] | null;
  onSetStartMeasure: (index: number) => void;
  onChordClick: (measureIndex: number, chordIndex: number) => void;
  onSubdivisionChange: (measureIndex: number, newSubdivision: number) => void;
  onPlayChord: (chord: string) => void;
  onCopyMeasure: (measureIndex: number) => void;
  onPasteMeasure: (measureIndex: number) => void;
  onChordHover: (chord: string, element: HTMLElement) => void;
  onChordLeave: () => void;
  onLyricChange: (measureIndex: number, newLyric: string) => void;
  onInsertMeasures: (afterMeasureIndex: number) => void;
  onDeleteMeasure: (measureIndex: number) => void;
  timeSignature: TimeSignature;
  gigFontSize: number;
  lyricsFontSize?: number;
  inspectorMode?: 'piano' | 'guitar';
  inspectorEnabled?: boolean;
  chartHasFine?: boolean;
  chartHasToCoda?: boolean;
  voltaPosition?: import('./MeasureAnnotations').VoltaPosition;
  previousMeasureHasEndRepeat?: boolean;
}

const getIncenterPosition = (
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number }
): { top: string; left: string } => {
    const distance = (a: { x: number; y: number }, b: { x: number; y: number }) =>
        Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));

    const a = distance(p2, p3);
    const b = distance(p1, p3);
    const c = distance(p1, p2);

    const perimeter = a + b + c;
    if (perimeter === 0) return { top: '50%', left: '50%' };

    const incenter = {
        x: (a * p1.x + b * p2.x + c * p3.x) / perimeter,
        y: (a * p1.y + b * p2.y + c * p3.y) / perimeter,
    };

    return { top: `${incenter.y}%`, left: `${incenter.x}%` };
};

const SubdivisionButton: React.FC<{
    count: number,
    isActive: boolean,
    onClick: () => void
}> = ({ count, isActive, onClick }) => (
    <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            isActive
                ? 'bg-[#D4A853] text-[#271B13] shadow-md'
                : 'bg-[#56463C] hover:bg-[#6B5548] text-[#D3CCBD]'
        }`}
        aria-label={`Subdivide into ${count}`}
    >
        {count}
    </button>
);

const GigMeasureContent: React.FC<{ measure: MeasureType; fontSize: number }> = ({ measure, fontSize }) => {
    const chords = measure.chords;
    const subdivision = chords.length;

    // Calculate adaptive font size for each chord based on complexity and subdivision
    const getAdaptiveFontSize = (chord: string) => {
        if (!chord || chord === '') return fontSize;
        
        // Special handling for repeat symbols - use smaller, consistent size
        if (chord === '%' || chord === '/' || chord === 'x') {
            return Math.max(fontSize * 0.8, fontSize * 0.6); // 60-80% of base size
        }
        
        // Analyze chord complexity
        const getChordComplexity = (chord: string) => {
            let complexity = 0;
            
            // Check for bass chord (adds significant visual complexity)
            if (chord.includes('/') && !chord.includes('6/9')) complexity += 2;
            
            // Check for accidentals
            if (chord.includes('#') || chord.includes('♯') || chord.includes('b') || chord.includes('♭')) complexity += 1;
            
            // Check for extensions and alterations
            if (chord.match(/\d/)) complexity += 1; // Numbers like 7, 9, 11, 13
            if (chord.includes('maj')) complexity += 1;
            if (chord.includes('add') || chord.includes('sus')) complexity += 1;
            if (chord.includes('dim') || chord.includes('aug')) complexity += 1;
            if (chord.match(/[♯#♭b][579]/)) complexity += 1; // Altered extensions
            
            return complexity;
        };

        const complexity = getChordComplexity(chord);
        
        // Base font size with complexity adjustment
        let adaptiveSize = fontSize;
        
        // Reduce size for more complex chords
        if (complexity >= 3) {
            adaptiveSize = fontSize * 0.7; // Very complex chords
        } else if (complexity >= 2) {
            adaptiveSize = fontSize * 0.8; // Complex chords
        } else if (complexity >= 1) {
            adaptiveSize = fontSize * 0.9; // Moderately complex chords
        }
        
        // Further adjust for subdivision (more chords = less space)
        if (subdivision >= 4) {
            adaptiveSize *= 0.85; // Tight fit for 4 chords
        } else if (subdivision >= 3) {
            adaptiveSize *= 0.9; // Moderate fit for 3 chords
        }
        
        // Ensure minimum readability
        const MIN_GIG_FONT_SIZE = Math.max(10, fontSize * 0.5);
        const MAX_GIG_FONT_SIZE = fontSize * 1.1;
        
        return Math.max(MIN_GIG_FONT_SIZE, Math.min(adaptiveSize, MAX_GIG_FONT_SIZE));
    };

    if (subdivision === 1) {
        const adaptiveSize = getAdaptiveFontSize(chords[0] || '');
        return (
            <div 
              className="absolute inset-0 flex items-center justify-center"
              data-debug-id="Level-3:GigSingle"
            >
                <StyledChord chord={chords[0] || ''} baseFontSize={adaptiveSize} color="#DADEE0" />
            </div>
        );
    }

    const XDivider = () => (
        <svg 
          className="absolute inset-0 w-full h-full" 
          aria-hidden="true" 
          stroke="rgba(218,222,224,0.25)"
          strokeWidth="1"
          data-debug-id="Level-3:GigXDivider"
        >
            <line x1="0" y1="0" x2="100%" y2="100%" />
            <line x1="100%" y1="0" x2="0" y2="100%" />
        </svg>
    );
    
    const DiagonalDivider = () => (
        <svg 
          className="absolute inset-0 w-full h-full" 
          aria-hidden="true" 
          stroke="rgba(218,222,224,0.25)"
          strokeWidth="1"
          data-debug-id="Level-3:GigDiagonalDivider"
        >
            <line x1="0" y1="100%" x2="100%" y2="0" />
        </svg>
    );

    const ChordDisplay: React.FC<{ chord: string; position: React.CSSProperties; index?: number }> = ({ chord, position, index }) => {
        const adaptiveSize = getAdaptiveFontSize(chord);
        return (
            <div 
              className="absolute" 
              style={position}
              data-debug-id={`Level-3:GigChord${index !== undefined ? `-${index + 1}` : ''}`}
            >
                <StyledChord chord={chord || ''} baseFontSize={adaptiveSize} color="#DADEE0" />
            </div>
        );
    };

    const centerTransform = { transform: 'translate(-50%, -50%)' };

    if (subdivision === 2) {
        // Top-left triangle: vertices at (0,0), (100,0), (0,100)
        const pos1 = getIncenterPosition({ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 100 });
        // Bottom-right triangle: vertices at (100,0), (100,100), (0,100)
        const pos2 = getIncenterPosition({ x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 });
        
        return (
            <>
                <DiagonalDivider />
                <ChordDisplay chord={chords[0] || ''} position={{ ...pos1, ...centerTransform }} index={0} />
                <ChordDisplay chord={chords[1] || ''} position={{ ...pos2, ...centerTransform }} index={1} />
            </>
        );
    }
    
    if (subdivision === 3) {
        return (
            <>
                <XDivider />
                <ChordDisplay chord={chords[0] || ''} position={{ top: '25%', left: '25%', ...centerTransform }} index={0} />
                <ChordDisplay chord={chords[1] || ''} position={{ top: '25%', left: '75%', ...centerTransform }} index={1} />
                <ChordDisplay chord={chords[2] || ''} position={{ top: '75%', left: '50%', ...centerTransform }} index={2} />
            </>
        );
    }

    if (subdivision === 4) {
        const center = { x: 50, y: 50 };
        const tl = { x: 0, y: 0 };
        const tr = { x: 100, y: 0 };
        const bl = { x: 0, y: 100 };
        const br = { x: 100, y: 100 };

        const pos1 = getIncenterPosition(tl, bl, center); // Left
        const pos2 = getIncenterPosition(tl, tr, center); // Top
        const pos3 = getIncenterPosition(bl, br, center); // Bottom
        const pos4 = getIncenterPosition(tr, br, center); // Right

         return (
            <>
                <XDivider />
                <ChordDisplay chord={chords[0] || ''} position={{ ...pos1, ...centerTransform }} index={0} />
                <ChordDisplay chord={chords[1] || ''} position={{ ...pos2, ...centerTransform }} index={1} />
                <ChordDisplay chord={chords[2] || ''} position={{ ...pos3, ...centerTransform }} index={2} />
                <ChordDisplay chord={chords[3] || ''} position={{ ...pos4, ...centerTransform }} index={3} />
            </>
        );
    }

    return null; // Fallback for unsupported subdivisions
};


const MIN_LYRICS_FONT_SIZE = 10;
const MAX_LETTER_SPACING_REDUCTION = -0.5; // Tuned for better readability

interface FittedLyricsProps {
  lyrics: string;
  onLyricChange: (newLyric: string) => void;
  mode: AppMode;
  baseFontSize: number;
  placeholder?: string;
  measureIndex: number;
}

const FittedLyrics: React.FC<FittedLyricsProps> = ({ lyrics, onLyricChange, mode, baseFontSize, placeholder, measureIndex }) => {
  const textRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  // Measure the container width reliably using ResizeObserver
  React.useLayoutEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) return;

    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    observer.observe(containerElement);
    return () => observer.disconnect();
  }, []);

  // Apply fitting logic when lyrics, font size, or container width changes
  React.useLayoutEffect(() => {
    const textElement = textRef.current;
    if (!textElement || containerWidth === 0) return;

    // 1. Reset styles to measure the text in its natural state
    textElement.style.letterSpacing = 'normal';
    textElement.style.fontSize = `${baseFontSize}px`;
    textElement.style.transform = 'scaleX(1)';

    const effectiveContainerWidth = containerWidth - 8; // A little horizontal padding
    let textWidth = textElement.scrollWidth;

    if (textWidth <= effectiveContainerWidth) {
      return; // Already fits, no adjustments needed
    }

    // Stage 1: Reduce letter spacing
    for (let ls = -0.1; ls >= MAX_LETTER_SPACING_REDUCTION; ls -= 0.1) {
        textElement.style.letterSpacing = `${ls.toFixed(1)}px`;
        textWidth = textElement.scrollWidth;
        if (textWidth <= effectiveContainerWidth) {
          return;
        }
    }

    // Stage 2: Reduce font size
    for (let fs = baseFontSize - 1; fs >= MIN_LYRICS_FONT_SIZE; fs -= 1) {
        textElement.style.fontSize = `${fs}px`;
        textWidth = textElement.scrollWidth;
        if (textWidth <= effectiveContainerWidth) {
          return;
        }
    }

    // Stage 3: Compress with scaleX as a last resort
    if (textWidth > effectiveContainerWidth) {
        const scale = effectiveContainerWidth / textWidth;
        textElement.style.transform = `scaleX(${scale})`;
    }
    
  }, [lyrics, baseFontSize, containerWidth]);

  // This effect safely synchronizes the div's content with the lyrics prop from React state
  React.useEffect(() => {
    if (textRef.current && textRef.current.textContent !== lyrics) {
      textRef.current.textContent = lyrics;
    }
  }, [lyrics]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    onLyricChange(e.currentTarget.textContent || '');
  };
  
  const isEdition = mode === 'edition';
  const editorClasses = isEdition
    ? 'bg-[#362820] rounded-md py-2 px-2'
    : 'py-1 px-1';
  
  // Dynamic height based on font size to prevent clipping
  const minHeight = Math.max(baseFontSize + 8, isEdition ? 32 : 20); // Add padding for font size
  const containerHeightStyle = { minHeight: `${minHeight}px` };

  return (
    <div
      ref={containerRef}
      className={`text-gray-300 flex items-center justify-center overflow-hidden ${
        mode === 'edition' ? 'mt-1' : ''
      }`}
      style={containerHeightStyle}
      data-debug-id={`Level-3:LyricsContainer-${measureIndex + 1}`}
    >
      <div
        ref={textRef}
        contentEditable={isEdition}
        onInput={isEdition ? handleInput : undefined}
        onClick={isEdition ? (e) => e.stopPropagation() : undefined}
        suppressContentEditableWarning={true}
        className={`focus:outline-none whitespace-nowrap ${editorClasses}`}
        style={{ transformOrigin: 'center' }}
        data-placeholder={placeholder}
        data-debug-id={`Level-3:LyricsText-${measureIndex + 1}`}
      >
        {/* Content is managed by the useEffect hook to prevent React/DOM conflicts */}
      </div>
    </div>
  );
};


const RepeatBar: React.FC<{ type: 'start' | 'end' }> = ({ type }) => {
    const thickLine = <div className="w-0.5 bg-gray-400" />;
    const thinLine = <div className="w-px bg-gray-400" />;
    const dots = (
        <div className="flex flex-col h-full justify-center items-center px-px">
            <div className="w-1 h-1 bg-gray-400 rounded-full mb-1" />
            <div className="w-1 h-1 bg-gray-400 rounded-full mt-1" />
        </div>
    );

    const content = type === 'start' 
        ? <>{thickLine}{thinLine}{dots}</>
        : <>{dots}{thinLine}{thickLine}</>;
        
    return (
      <div className="h-full flex items-stretch gap-px min-w-[10px] flex-shrink-0">
          {content}
        </div>
    );
};


const DoubleBar: React.FC = () => (
  <div className="h-full flex items-stretch gap-px min-w-[5px] flex-shrink-0">
        <div className="w-px bg-gray-400" />
        <div className="w-px bg-gray-400" />
    </div>
);

const FinalBar: React.FC = () => (
  <div className="h-full flex items-stretch gap-px min-w-[6px] flex-shrink-0">
        <div className="w-px bg-gray-400" />
        <div className="w-1.5 bg-gray-400" />
    </div>
);

const Measure: React.FC<MeasureProps> = ({
  mode,
  measure,
  measureIndex,
  activeChord,
  isPlaying,
  playingChordIndex,
  isStartMeasure,
  invalidChords,
  clipboard,
  onSetStartMeasure,
  onChordClick,
  onSubdivisionChange,
  onPlayChord,
  onCopyMeasure,
  onPasteMeasure,
  onChordHover,
  onChordLeave,
  onLyricChange,
  onInsertMeasures,
  onDeleteMeasure,
  gigFontSize,
  lyricsFontSize,
  inspectorMode = 'piano',
  inspectorEnabled = false,
  chartHasFine = false,
  chartHasToCoda = false,
  voltaPosition,
  previousMeasureHasEndRepeat = false,
}) => {
  const currentSubdivision = measure.chords.length;
  const isGigMode = mode === 'gig' || mode === 'sandbox'; // Gig mode uses compact layout (includes sandbox)
  const isSelected = activeChord?.measureIndex === measureIndex; // Check if this measure is selected
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [inspectorButtonRef, setInspectorButtonRef] = useState<HTMLButtonElement | null>(null);

  // Detect mobile/tablet
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window;
      setIsMobileOrTablet(width <= 1024 || isTouchDevice);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Handle inspector button click
  const handleInspectorClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const firstChord = measure.chords[0];
    if (firstChord && firstChord.trim() && inspectorButtonRef) {
      onChordHover(firstChord, inspectorButtonRef);
    }
  };

  let borderClasses = 'border-[rgba(86,70,60,0.5)]';
  if (isPlaying) {
      borderClasses = 'border-[#D4A853] ring-2 ring-[#D4A853] ring-opacity-75';
  } else if (isStartMeasure) {
      borderClasses = 'border-[#9DDB17] ring-2 ring-[#9DDB17] ring-opacity-75';
  }

  const modeSpecificClasses = isGigMode ? 'aspect-square' : 'min-h-[168px]';
  const paddingClass = isGigMode ? 'p-1' : 'p-2';
  const placeholder = measureIndex === 0 ? "Type your lyrics here..." : undefined;
  const hasExplicitBarline = !!measure.annotations?.barlineLeft || !!measure.annotations?.barlineRight;
  const showStartRepeat = !!measure.annotations?.startRepeat;
  const showEndRepeat = !!measure.annotations?.endRepeat;
  const showLeadingDoubleBar = (measure.annotations?.barlineLeft === 'double' && !showStartRepeat) || (
    !hasExplicitBarline &&
    !!measure.annotations?.doubleBarline &&
    (!!measure.annotations?.ending || previousMeasureHasEndRepeat) &&
    !showStartRepeat
  );
  const showLeadingFinalBar = measure.annotations?.barlineLeft === 'final' && !showStartRepeat;
  const showTrailingDoubleBar = (measure.annotations?.barlineRight === 'double' && !showEndRepeat) || (
    !hasExplicitBarline && !!measure.annotations?.doubleBarline && !showLeadingDoubleBar && !showEndRepeat
  );
  const showTrailingFinalBar = (measure.annotations?.barlineRight === 'final' && !showEndRepeat) || (
    !hasExplicitBarline && !!measure.annotations?.finalBarline && !showEndRepeat
  );

  return (
    <div 
      className={`group rounded-lg shadow-md border flex flex-col transition-all duration-300 relative ${borderClasses} ${modeSpecificClasses} ${paddingClass}`}
      style={{ backgroundColor: isGigMode ? '#5E5451' : t.measureBg }}
      data-debug-id={`Level-2:Measure-${measureIndex + 1}`}
    >
      <div 
        role="button"
        onClick={() => onSetStartMeasure(measureIndex)}
        aria-label={`Set measure ${measureIndex + 1} as playback start`}
        className={`flex justify-between items-center ${isGigMode ? 'h-4' : ''} rounded-md cursor-pointer hover:bg-[#362820]/50 relative`}
        data-debug-id={`Level-2:MeasureHeader-${measureIndex + 1}`}
      >
        {/* Ending bracket underline - positioned after ending numbers in Gig Mode */}
        {isGigMode && measure.annotations && (measure.annotations.ending || measure.annotations.multipleEnding) && (
          <div className={`absolute bottom-0 right-0 border-b border-gray-500 ${
            measure.annotations.section || measure.annotations.rehearsalLetter ? 'left-10' : 'left-4'
          }`}></div>
        )}
        
        <div 
            className={`flex-shrink-0 font-bold select-none rounded-md ${
              isGigMode
                ? 'text-[7px] p-0 leading-none'  // 13% bigger: 7px instead of 6px
                : 'text-sm text-gray-400 p-1'
            }`}
            style={isGigMode ? { color: 'rgba(218,222,224,0.5)' } : undefined}
            data-debug-id={`Level-3:MeasureNumber-${measureIndex + 1}`}
        >
            {measureIndex + 1}
        </div>

        {/* Mobile/Tablet Inspector Button */}
        {isMobileOrTablet && inspectorEnabled && measure.chords[0]?.trim() && (
          <button
            ref={setInspectorButtonRef}
            onClick={handleInspectorClick}
            className="ml-1 w-8 h-8 flex items-center justify-center hover:bg-[#362820]/30 rounded transition-colors"
            aria-label="Show chord diagram"
          >
            {inspectorMode === 'guitar' ? (
              <svg fill="#ff9500" viewBox="-62.4 -62.4 384.80 384.80" className="w-7 h-7">
                <path d="M256.575,24.068L235.932,3.425c-1.9-1.9-4.981-1.9-6.881,0l-20.643,20.643c-1.899,1.899-1.9,4.979-0.002,6.879 l-57.178,57.178c-18.815-13.19-39.733-15.744-50.633-4.844c-2.47,2.47-4.249,5.456-5.371,8.815 c-0.952,2.787-2.043,5.646-3.387,8.497c-1.575,3.339-3.655,6.511-6.425,9.281c-3.879,3.879-8.567,6.389-13.444,7.956 c-10.148,3.26-22.628,5.638-33.076,8.792c-3.875,0.966-7.588,2.353-11.078,4.166c-1.686,0.828-3.216,1.715-4.546,2.678 c0.001,0.001,0.003,0.003,0.004,0.004c-2.486,1.667-4.828,3.579-6.995,5.745C6.647,148.846,1.985,161.96,2,176 c0.02,19.118,8.712,40.007,25.35,56.646C44.045,249.341,64.83,258.038,84,258c13.967-0.028,27.194-4.694,36.78-14.28 c2.178-2.178,4.097-4.533,5.77-7.033c0.932-1.292,1.793-2.767,2.597-4.392c1.884-3.602,3.32-7.438,4.298-11.451 c3.117-10.395,5.482-22.753,8.716-32.819c1.567-4.877,4.077-9.565,7.956-13.444c2.77-2.77,5.942-4.85,9.281-6.425 c2.806-1.324,5.62-2.4,8.366-3.341c3.413-1.119,6.448-2.909,8.951-5.412c10.901-10.901,8.346-31.819-4.844-50.634l57.178-57.178 l0.002,0.002c1.9,1.9,4.98,1.9,6.881,0l20.643-20.643C258.475,29.049,258.475,25.968,256.575,24.068z M84.605,209.906L50.2,175.501 l6.881-6.881l34.405,34.405L84.605,209.906z M135.618,150.183c-7.125,7.125-18.678,7.125-25.804,0s-7.125-18.678,0-25.804 c7.125-7.125,18.678-7.125,25.804,0S142.743,143.057,135.618,150.183z"/>
              </svg>
            ) : (
              <svg viewBox="-25.6 -25.6 115.20 115.20" className="w-7 h-7">
                <path fill="#99c5ff" d="M60,0H4C1.789,0,0,1.789,0,4v56c0,2.211,1.789,4,4,4h56c2.211,0,4-1.789,4-4V4C64,1.789,62.211,0,60,0z M15,62H4c-1.104,0-2-0.896-2-2V4c0-1.104,0.641-2,2-2h7v39c0,0.553,0.447,1,1,1h3V62z M31,62H17V42h3c0.553,0,1-0.447,1-1V2h6v39 c0,0.553,0.447,1,1,1h3V62z M47,62H33V42h3c0.553,0,1-0.447,1-1V2h6v39c0,0.553,0.447,1,1,1h3V62z M62,60c0,1.104-0.896,2-2,2H49V42 h3c0.553,0,1-0.447,1-1V2h7c1.016,0,2,0.896,2,2V60z"/>
              </svg>
            )}
          </button>
        )}
        
        {/* In Gig Mode: Show annotations inline in header to save space */}
        {isGigMode && measure.annotations ? (
          <div className="flex-1 flex items-center justify-start gap-1 text-xs leading-tight pl-2" data-debug-id={`Level-3:HeaderAnnotations-${measureIndex + 1}`}>
            <MeasureAnnotations 
              annotations={measure.annotations} 
              measureIndex={measureIndex}
              compact={true}
              fontSize={gigFontSize}
              chartHasFine={chartHasFine}
              chartHasToCoda={chartHasToCoda}
            />
          </div>
        ) : (
          <div className="flex-1" data-debug-id={`Level-3:HeaderSpacer-${measureIndex + 1}`}></div>
        )}

        {mode === 'edition' && (
          <>
            <div className={`flex items-center gap-2 transition-opacity ${isSelected && isMobileOrTablet ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                <button
                    onClick={(e) => { e.stopPropagation(); onCopyMeasure(measureIndex); }}
                    className="p-1.5 rounded-md hover:bg-[#362820] text-gray-400 hover:text-[#D4A853] active:scale-95 active:bg-[#362820] transition-transform"
                    aria-label={`Copy measure ${measureIndex + 1}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onPasteMeasure(measureIndex); }}
                    disabled={!clipboard}
                    className="p-1.5 rounded-md hover:bg-[#362820] text-gray-400 hover:text-[#D4A853] active:scale-95 active:bg-[#362820] transition-transform disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed disabled:active:scale-100"
                    aria-label={`Paste into measure ${measureIndex + 1}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                </button>
            </div>
            
            <div className="flex-grow"></div> 

            <div className="flex items-center gap-1 flex-shrink-0">
              {[1, 2, 3, 4].map(count => (
                <SubdivisionButton 
                    key={count}
                    count={count}
                    isActive={currentSubdivision === count}
                    onClick={() => onSubdivisionChange(measureIndex, count)}
                />
              ))}
            </div>
          </>
        )}
      </div>
      
      {/* Show annotations in Edition mode, or in Gig mode if no annotations to put in header */}
      {!isGigMode && (
        <MeasureAnnotations 
          annotations={measure.annotations} 
          measureIndex={measureIndex}
          chartHasFine={chartHasFine}
          chartHasToCoda={chartHasToCoda}
          voltaPosition={voltaPosition}
        />
      )}

      <div 
        className="flex-grow flex items-stretch gap-1"
        data-debug-id={`Level-2:ChordArea-${measureIndex + 1}`}
      >
        {showLeadingFinalBar && (
          <FinalBar />
        )}
        {showLeadingDoubleBar && (
          <DoubleBar />
        )}
        {showStartRepeat && (
          <RepeatBar 
            type="start" 
            data-debug-id={`Level-3:StartRepeat-${measureIndex + 1}`}
          />
        )}
        <div 
          className="flex-grow rounded-sm overflow-hidden relative"
          style={{ backgroundColor: isGigMode ? 'transparent' : 'rgba(26, 26, 26, 0.5)' }}
          data-debug-id={`Level-3:ChordContent-${measureIndex + 1}`}
        >
            {(isGigMode) ? (
            <GigMeasureContent 
              measure={measure} 
              fontSize={gigFontSize} 
              data-debug-id={`Level-3:GigContent-${measureIndex + 1}`}
            />
            ) : (
            <div 
              className={`grid ${currentSubdivision > 1 ? `grid-cols-${currentSubdivision}` : 'grid-cols-1'} gap-px min-h-full`}
              data-debug-id={`Level-3:EditionGrid-${measureIndex + 1}`}
            >
                {measure.chords.map((chord, chordIndex) => (
                <ChordCell
                    key={chordIndex}
                    mode={mode}
                    chord={chord || ''}
                    measureIndex={measureIndex}
                    chordIndex={chordIndex}
                    isActive={
                    activeChord?.measureIndex === measureIndex &&
                    activeChord?.chordIndex === chordIndex
                    }
                    isBeingPlayed={playingChordIndex === chordIndex}
                    error={invalidChords[`${measureIndex}-${chordIndex}`]}
                    onClick={() => onChordClick(measureIndex, chordIndex)}
                    onPlayChord={onPlayChord}
                    onChordHover={isMobileOrTablet ? () => {} : onChordHover}
                    onChordLeave={onChordLeave}
                />
                ))}
            </div>
            )}
        </div>
        {showEndRepeat && (
          <RepeatBar 
            type="end" 
            data-debug-id={`Level-3:EndRepeat-${measureIndex + 1}`}
          />
        )}
        {showTrailingDoubleBar && (
          <DoubleBar />
        )}
        {showTrailingFinalBar && (
          <FinalBar />
        )}
      </div>

      {/* Insert Measures Button - only show in edition mode */}
      {mode === 'edition' && (
        <button
          onClick={() => onInsertMeasures(measureIndex)}
          className="absolute bottom-1 right-1 w-6 h-6 bg-[#362820] hover:bg-[#4A3828]
                   text-[#D3CCBD] hover:text-[#F4F1EB] rounded-full flex items-center justify-center text-sm font-bold
                   transition-colors duration-200 shadow-sm z-10"
          title={`Insert measures after measure ${measureIndex + 1}`}
        >
          +
        </button>
      )}

      {/* Delete Measure Button - only show in edition mode */}
      {mode === 'edition' && (
        <button
          onClick={() => onDeleteMeasure(measureIndex)}
          className="absolute bottom-1 left-1 w-6 h-6 bg-[#362820] hover:bg-[#4A3828]
                   text-[#D3CCBD] hover:text-[#F4F1EB] rounded-full flex items-center justify-center text-sm font-bold
                   transition-colors duration-200 shadow-sm z-10"
          title={`Delete measure ${measureIndex + 1}`}
        >
          −
        </button>
      )}

      <FittedLyrics
        lyrics={measure.lyrics || ''}
        onLyricChange={(newLyric) => onLyricChange(measureIndex, newLyric)}
        mode={mode}
        baseFontSize={lyricsFontSize || 14}
        placeholder={placeholder}
        data-debug-id={`Level-3:Lyrics-${measureIndex + 1}`}
        measureIndex={measureIndex}
      />
    </div>
  );
};

export default Measure;
